import { useTranslation } from "react-i18next";
import { Add } from "iconsax-react";
import { useContext, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LocaleContext } from "../../../routing/LangRouter";
import { toast } from "react-toastify";

//import general components
import BaseDialog from "../../common/base-dialog/base-dialog";
import FlightCard from "../../common/minimal-flight-card/minimal-flight-card";
import { BaseButton } from "../../common/base-button/base-button";
import { DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import SelectedItem from "../../common/selected-item/selected-item";

//import view components
import AvailabilityNotificationResult from "./notification/availability-notification-result";

//import styles
import "../flight/domestic/flight-item.scss";

//import constants
import BASE_URL from "../../../constants/baseUrls";

//import utils
import { convertToJalali, formatPersianDate } from "../../../utils/date";

export default function AvailabiltyModal({ isModalOpen, setIsModalOpen, flightItem, title, modalType, pageUrl }) {
    const { t } = useTranslation();
    const [setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { locale } = useContext(LocaleContext);

    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [responseData, setResponseData] = useState({});

    const generalInformation = JSON.parse(sessionStorage.getItem("generalInformation")) || {};
    const intFlightInformation = JSON.parse(sessionStorage.getItem("intFlightInformation")) || {};
    const selectedIntFlight = JSON.parse(sessionStorage.getItem("selectedIntFlight")) || {};
    const accessToken = localStorage.getItem("accessToken");
    const { isFlightInternational } = generalInformation;

    const {
        departure_date,
        departure_time,
        arrival_date,
        destination_time,
        flight_number,
        cobin,
        adult_price,
        tag,
        departure_city_id,
        destination_city_id,
        type,
        airline_id,
        aircraft,
    } = flightItem;

    const adultPrice = adult_price ?? selectedIntFlight?.fares?.adult?.price;
    const flightSegment = selectedIntFlight?.segments?.[0];
    const airlineId = airline_id ?? flightSegment?.airline?.id;
    const cabinId = cobin === "Economy" ? 2 : 3;

    const flightData = {
        departure_date: departure_date ?? flightSegment?.departure_date,
        departure_time: departure_time ?? flightSegment?.departure_time,
        destination_date: arrival_date ?? flightSegment?.arrival_date,
        destination_time: destination_time ?? flightSegment?.destination_time,
        flight_number: flight_number ?? flightSegment?.flight_number,
        airlines: tag === "ch118" ? [aircraft] : [airlineId],
        cabin_type: cabinId,
        max_price: adultPrice,
        tag,
        departure_city: departure_city_id,
        destination_city: destination_city_id,
        departure_airport: intFlightInformation?.departure?.id,
        destination_airport: intFlightInformation?.destination?.id,
        flight_type: type,
    };

    const untilDate = convertToJalali(responseData?.departure_date);
    const untilTime = responseData?.departure_time;

    const submitAvailabiltyNotification = async event => {
        event.preventDefault();
        try {
            setIsLoading(true);
            const response = await axios.post(`${BASE_URL}/notification/manage/`, flightData, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
            });
            if (response.status === 200) {
                setIsSubmitted(true);
                setResponseData(response?.data?.data);
            }
        } catch (error) {
            if (error.response.status === 401) {
                localStorage.clear();
                setSearchParams({ action: "login" });
            } else {
                toast.error(t("errorMessage"), { toastId: "custom-id-yes" });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const submitAvailabilitySetting = ticket => {
        sessionStorage.setItem(
            "generalInformation",
            JSON.stringify({
                ...generalInformation,
                isAutomaticReserve: true,
            }),
        );
        isFlightInternational
            ? sessionStorage.setItem("selectedIntFlight", JSON.stringify(ticket))
            : sessionStorage.setItem("selectedFlight", JSON.stringify(ticket));

        navigate(pageUrl);
    };

    const intFlightData = selectedIntFlight && {
        title: `${t("departureTicket")}  ${intFlightInformation?.returnDate ? t("Flight.And Return") : ""}`,
        name: [
            ...(selectedIntFlight?.segments?.map(segment =>
                locale === "en" ? segment?.departure?.city?.english : segment?.departure?.city?.persian,
            ) ?? []),
            selectedIntFlight?.segments?.[selectedIntFlight?.segments?.length - 1]?.destination?.city?.persian,
        ],
        date: (
            <Grid container item xs={12} direction="column">
                <span>
                    {t("departureDate")}:{" "}
                    {formatPersianDate(selectedIntFlight?.segments?.[0]?.departure_date, {
                        day: "numeric",
                        month: "long",
                        year: undefined,
                        weekday: "long",
                    })}{" "}
                    - {selectedIntFlight?.segments?.[0]?.departure_time.split(":", 2).join(":")}
                </span>
                {intFlightInformation?.returnDate && (
                    <span>
                        {t("returnDate")}:{" "}
                        {formatPersianDate(selectedIntFlight?.return_segments?.[0]?.departure_date, {
                            day: "numeric",
                            month: "long",
                            year: undefined,
                            weekday: "long",
                        })}{" "}
                        - {selectedIntFlight?.return_segments?.[0]?.departure_time.split(":", 2).join(":")}
                    </span>
                )}
            </Grid>
        ),
        type: "intFlight",
        details: selectedIntFlight,
        route: "international-flight",
    };

    return (
        <BaseDialog open={isModalOpen} handleClose={() => setIsModalOpen(false)} top="auto" maxWidth="sm">
            <DialogTitle display="flex" columnGap={1} alignItems="center">
                <Add className="close" size="32" color="var(--teal-color-18)" onClick={() => setIsModalOpen(false)} cursor="pointer" />
                <span>{title}</span>
            </DialogTitle>
            {isSubmitted ? (
                <DialogContent dividers={true}>
                    <AvailabilityNotificationResult date={untilDate} hour={untilTime} />
                </DialogContent>
            ) : (
                <>
                    <DialogContent dividers={true}>
                        {isFlightInternational && selectedIntFlight ? (
                            <SelectedItem data={intFlightData} />
                        ) : (
                            <FlightCard flightItem={flightItem} isClickable={false} type="suggest" />
                        )}
                    </DialogContent>

                    <DialogActions>
                        <BaseButton
                            text={t("General.Manual Setting")}
                            color="outline"
                            radius="radius-10"
                            size="medium"
                            handleClick={() => submitAvailabilitySetting(flightItem)}
                        />
                        {modalType === "notification" && (
                            <BaseButton
                                text={t("confirmation")}
                                color="primary"
                                radius="radius-10"
                                size="medium"
                                handleClick={submitAvailabiltyNotification}
                                loading={isLoading}
                            />
                        )}
                    </DialogActions>
                </>
            )}
        </BaseDialog>
    );
}
